import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import {
  ArrowLeft, TrendingUp, Users, FileText, Mail, Activity,
  Download as DownloadIcon, BarChart3, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const GOLD = '#C4A55A';
const GOLD_LIGHT = '#D4B56A';
const GOLD_DARK = '#8A6F35';
const PIE_COLORS = ['#C4A55A', '#8A6F35', '#D4B56A', '#6B5424', '#E5C97A', '#B8974E'];

const CATEGORY_LABELS = {
  factsheet: 'Factsheet',
  report: 'Bericht',
  presentation: 'Präsentation',
  legal: 'Rechtliches',
  project: 'Projekt',
  uncategorized: 'Ohne Kategorie'
};

const STATUS_LABELS = {
  new: 'Neu',
  read: 'Gelesen',
  done: 'Erledigt',
  archived: 'Archiviert'
};

const fmtCHF = (v) => `CHF ${(v || 0).toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return ''; }
};

const ChartCard = ({ title, description, icon: Icon, children, testId }) => (
  <Card data-testid={testId} className="bg-[#1a1a1a] border-[#C4A55A]/20">
    <CardHeader>
      <div className="flex items-start justify-between gap-2">
        <div>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            {Icon && <Icon className="h-5 w-5 text-[#C4A55A]" />}
            {title}
          </CardTitle>
          {description && (
            <CardDescription className="text-gray-400 mt-1">{description}</CardDescription>
          )}
        </div>
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const TooltipBox = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0a0a0a] border border-[#C4A55A]/40 rounded-lg p-3 shadow-xl">
      {label && <p className="text-xs text-gray-400 mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey} className="text-sm text-white">
          <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="text-[#C4A55A] font-semibold">
            {valueFormatter ? valueFormatter(p.value, p.dataKey) : p.value}
          </span>
        </p>
      ))}
    </div>
  );
};

export const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const exportRef = React.useRef(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    (async () => {
      try {
        const res = await adminAPI.getAnalytics();
        if (res?.success) setData(res.analytics);
      } catch (e) {
        toast.error(e.message || 'Fehler beim Laden der Auswertungen');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const handleExportPDF = async () => {
    if (!exportRef.current) return;
    try {
      setExporting(true);
      toast.info('PDF wird erstellt...');
      // Wait briefly so any in-flight animations settle
      await new Promise((r) => setTimeout(r, 200));

      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#0a0a0a',
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: exportRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      pdf.save(`loewenherz-auswertungen-${stamp}.pdf`);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      console.error(err);
      toast.error('PDF-Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
        Lädt Auswertungen...
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">
        Keine Daten verfügbar.
      </div>
    );
  }

  const totalNewInvestors = data.investorsSeries.reduce((s, x) => s + x.newInvestors, 0);
  const totalNewInvestment = data.investorsSeries.reduce((s, x) => s + x.investmentSum, 0);
  const totalNewMessages = data.messagesSeries.reduce((s, x) => s + x.messages, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-[#C4A55A] hover:text-[#D4B56A] mb-2"
              data-testid="analytics-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="h-9 w-9 text-[#C4A55A]" />
              Auswertungen
            </h1>
            <p className="text-gray-400 mt-1">Vollständige Übersicht über Investoren, Anfragen, Projekte und Downloads</p>
          </div>
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="analytics-export-pdf-btn"
            className="bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {exporting ? 'Erstelle PDF...' : 'Als PDF exportieren'}
          </Button>
        </div>

        <div ref={exportRef} className="bg-gradient-to-br from-black via-[#1a1a1a] to-black p-2 rounded-lg">

        {/* KPI Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryTile icon={Users} label="Neue Investoren (12M)" value={totalNewInvestors} accent />
          <SummaryTile icon={TrendingUp} label="Neues Volumen (12M)" value={fmtCHF(totalNewInvestment)} />
          <SummaryTile icon={Mail} label="Anfragen (12M)" value={totalNewMessages} />
          <SummaryTile icon={DownloadIcon} label="Downloads gesamt" value={data.totals?.downloadCount || 0} />
        </div>

        {/* Row 1: Investor growth + Investment volume */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard
            title="Investoren-Wachstum"
            description="Neue Registrierungen pro Monat (letzte 12 Monate)"
            icon={Users}
            testId="chart-investors-growth"
          >
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.investorsSeries}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
                <Tooltip content={<TooltipBox />} />
                <Area
                  type="monotone"
                  dataKey="newInvestors"
                  name="Neue Investoren"
                  stroke={GOLD}
                  strokeWidth={2}
                  fill="url(#goldFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Investitionsvolumen"
            description="Investment-Summe pro Monat"
            icon={TrendingUp}
            testId="chart-investment-volume"
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.investorsSeries}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<TooltipBox valueFormatter={fmtCHF} />} />
                <Bar dataKey="investmentSum" name="Volumen (CHF)" fill={GOLD} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Row 2: Messages + Projects by type */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <ChartCard
            title="Kontaktanfragen pro Monat"
            description="Letzte 12 Monate"
            icon={Mail}
            testId="chart-messages-monthly"
          >
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.messagesSeries}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
                <Tooltip content={<TooltipBox />} />
                <Line
                  type="monotone"
                  dataKey="messages"
                  name="Anfragen"
                  stroke={GOLD_LIGHT}
                  strokeWidth={2}
                  dot={{ fill: GOLD, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Anfragen-Status"
            description="Verteilung aller Anfragen"
            icon={Activity}
            testId="chart-messages-status"
          >
            {data.messagesByStatus.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Keine Anfragen vorhanden.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data.messagesByStatus.map((s) => ({
                      ...s,
                      name: STATUS_LABELS[s.status] || s.status,
                    }))}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    innerRadius={45}
                    paddingAngle={3}
                  >
                    {data.messagesByStatus.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipBox />} />
                  <Legend wrapperStyle={{ color: '#ccc', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Projekte nach Typ"
            description="Aktuelle Projekt-Verteilung"
            icon={FileText}
            testId="chart-projects-by-type"
          >
            {data.projectsByType.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Keine Projekte vorhanden.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data.projectsByType}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    innerRadius={45}
                    paddingAngle={3}
                  >
                    {data.projectsByType.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipBox />} />
                  <Legend wrapperStyle={{ color: '#ccc', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Row 3: Downloads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard
            title="Top 5 Downloads"
            description="Meistgeladene Dateien"
            icon={DownloadIcon}
            testId="chart-top-downloads"
          >
            {data.topDownloads.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Noch keine Downloads.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, data.topDownloads.length * 50)}>
                <BarChart
                  data={data.topDownloads}
                  layout="vertical"
                  margin={{ left: 10, right: 20 }}
                >
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                  <XAxis type="number" stroke="#888" fontSize={12} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="title"
                    stroke="#bbb"
                    fontSize={11}
                    width={180}
                    tick={{ fill: '#ddd' }}
                  />
                  <Tooltip content={<TooltipBox />} />
                  <Bar dataKey="downloads" name="Downloads" fill={GOLD} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Downloads nach Kategorie"
            description="Gesamt-Downloads je Kategorie"
            icon={FileText}
            testId="chart-downloads-by-category"
          >
            {data.downloadsByCategory.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Noch keine Dateien hochgeladen.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={data.downloadsByCategory.map((c) => ({
                    ...c,
                    label: CATEGORY_LABELS[c.category] || c.category,
                  }))}
                >
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                  <XAxis dataKey="label" stroke="#888" fontSize={12} />
                  <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
                  <Tooltip content={<TooltipBox />} />
                  <Legend wrapperStyle={{ color: '#ccc', fontSize: 12 }} />
                  <Bar dataKey="files" name="Dateien" fill={GOLD_DARK} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="downloads" name="Downloads" fill={GOLD} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Row 4: Top investors table + Activity feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Top 10 Investoren"
            description="Größte Investments"
            icon={Users}
            testId="table-top-investors"
          >
            {data.topInvestors.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Noch keine Investoren registriert.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-[#C4A55A]/15">
                      <th className="py-2 font-medium">#</th>
                      <th className="py-2 font-medium">Name</th>
                      <th className="py-2 font-medium text-right">Investment</th>
                      <th className="py-2 font-medium text-right">Seit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topInvestors.map((inv, i) => (
                      <tr key={i} className="border-b border-[#C4A55A]/10 hover:bg-[#C4A55A]/5">
                        <td className="py-2.5 text-gray-500">{i + 1}</td>
                        <td className="py-2.5 text-white">
                          <div>{inv.name}</div>
                          <div className="text-xs text-gray-500">{inv.email}</div>
                        </td>
                        <td className="py-2.5 text-right text-[#C4A55A] font-semibold whitespace-nowrap">
                          {fmtCHF(inv.investment)}
                        </td>
                        <td className="py-2.5 text-right text-gray-400 whitespace-nowrap">
                          {fmtDate(inv.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>

          <ChartCard
            title="Live-Aktivität"
            description="Letzte 10 Aktionen"
            icon={Activity}
            testId="feed-recent-activity"
          >
            {data.recentActivity.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center">Noch keine Aktivität.</p>
            ) : (
              <ul className="space-y-3">
                {data.recentActivity.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-[#C4A55A]/10">
                    <div className={`p-2 rounded-lg shrink-0 ${
                      a.type === 'investor' ? 'bg-emerald-500/10 border border-emerald-500/30'
                        : 'bg-[#C4A55A]/10 border border-[#C4A55A]/30'
                    }`}>
                      {a.type === 'investor'
                        ? <Users className="h-4 w-4 text-emerald-400" />
                        : <Mail className="h-4 w-4 text-[#C4A55A]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{a.title}</p>
                      <p className="text-xs text-gray-500 truncate">{a.subtitle}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {fmtDateTime(a.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ChartCard>
        </div>
        </div>{/* end exportRef */}
      </div>
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl p-5 border ${
    accent ? 'bg-gradient-to-br from-[#C4A55A]/15 to-[#1a1a1a] border-[#C4A55A]/40' : 'bg-[#1a1a1a] border-[#C4A55A]/20'
  }`}>
    <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide mb-2">
      <Icon className="h-3.5 w-3.5 text-[#C4A55A]" />
      {label}
    </div>
    <div className="text-2xl md:text-3xl font-bold text-white">{value}</div>
  </div>
);
